import { api } from "@/redux/api";

import {
	ForgotPasswordRequestSchema,
	ForgotPasswordType,
} from "../pages/ForgotPassword/schema/forgot-password";
import {
	FetchMeResponseSchema,
	GoogleAuth,
	GoogleAuthSchema,
	LoginRequestSchema,
	LoginResponse,
	LoginResponseSchema,
	LoginType,
	User,
} from "../pages/Login/schema/login";
import { RegisterRequestSchema, RegisterType } from "../pages/Register/schema/register";
import {
	SetPasswordBody,
	SetPasswordRequestSchema,
	SetPasswordResponse,
	SetPasswordResponseSchema,
} from "../pages/SetPassword/schema/set-password";
import {
	ResendTokenRequestSchema,
	ResendTokenResponse,
	ResendTokenResponseSchema,
	ValidateTokenResponse,
	ValidateTokenResponseSchema,
} from "../pages/SetPassword/schema/validate-token";
import {
	ChangePasswordRequestSchema,
	ChangePasswordBody,
} from "../pages/ChangePassword/schema/set-password";
import {
	ConfirmEmailType,
	ConfirmPasswordSchema,
} from "../pages/ConfirmEmail/schema/confirm-password";
import {
	VerifyEmailBody,
	VerifyEmailRequestSchema,
	VerifyEmailResponse,
	VerifyEmailResponseSchema,
} from "../pages/VerifyEmail/schema/verify-email";

export const authApi = api.injectEndpoints({
	endpoints: (builder) => ({
		fetchMe: builder.query<User, string>({
			query: (token) => ({
				url: "users/me",
				headers: {
					Authorization: `Bearer ${token}`,
				},
				responseSchema: FetchMeResponseSchema,
			}),
			providesTags: ["Me"],
		}),
		login: builder.mutation<LoginResponse, LoginType>({
			query: (body) => ({
				url: "users/login",
				method: "POST",
				body,
				requestSchema: LoginRequestSchema,
				responseSchema: LoginResponseSchema,
			}),
		}),
		googleAuth: builder.mutation<LoginResponse, GoogleAuth>({
			query: (body) => ({
				url: "users/google-auth",
				method: "POST",
				body,
				requestSchema: GoogleAuthSchema,
				responseSchema: LoginResponseSchema,
			}),
		}),
		registerAccount: builder.mutation<LoginResponse, Omit<RegisterType, "termsAndConditions">>({
			query: (body) => ({
				url: "users/register",
				method: "POST",
				body,
				requestSchema: RegisterRequestSchema,
				responseSchema: LoginResponseSchema,
			}),
		}),
		resetPassword: builder.mutation<void, ForgotPasswordType>({
			query: (body) => ({
				url: "users/reset-password",
				method: "POST",
				body,
				requestSchema: ForgotPasswordRequestSchema,
			}),
		}),
		setPassword: builder.mutation<SetPasswordResponse, SetPasswordBody>({
			query: (body) => ({
				url: "users/set-password",
				method: "POST",
				body,
				requestSchema: SetPasswordRequestSchema,
				responseSchema: SetPasswordResponseSchema,
			}),
		}),
		changePasswordWithToken: builder.mutation<SetPasswordResponse, ChangePasswordBody>({
			query: (body) => ({
				url: "users/set-password",
				method: "POST",
				body,
				requestSchema: ChangePasswordRequestSchema,
				responseSchema: SetPasswordResponseSchema,
			}),
		}),
		confirmEmail: builder.mutation<LoginResponse, ConfirmEmailType>({
			query: (body) => ({
				url: "users/confirm-email",
				method: "POST",
				body,
				requestSchema: ConfirmPasswordSchema,
			}),
		}),
		verifyEmail: builder.mutation<VerifyEmailResponse, VerifyEmailBody>({
			query: (body) => ({
				url: "users/verify-email",
				method: "POST",
				body,
				requestSchema: VerifyEmailRequestSchema,
				responseSchema: VerifyEmailResponseSchema,
			}),
			invalidatesTags: ["Me"],
		}),
		resendVerificationEmail: builder.mutation<VerifyEmailResponse, void>({
			query: () => ({
				url: "users/resend-verification-email",
				method: "POST",
				responseSchema: VerifyEmailResponseSchema,
			}),
		}),
		validateInvitationToken: builder.query<ValidateTokenResponse, string>({
			query: (token) => ({
				url: "token/validate",
				params: { token },
				responseSchema: ValidateTokenResponseSchema,
			}),
		}),
		resendInvitationToken: builder.mutation<ResendTokenResponse, string>({
			query: (token) => ({
				url: "token/resend",
				method: "POST",
				body: { token },
				requestSchema: ResendTokenRequestSchema,
				responseSchema: ResendTokenResponseSchema,
			}),
		}),
		authTwoFactorCode: builder.mutation<LoginResponse, { code: string; token: string }>({
			query: ({ code, token }) => ({
				url: "2fa/authenticate",
				method: "POST",
				body: { code },
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}),
		}),
	}),
});

export const {
	useLoginMutation,
	useResetPasswordMutation,
	useSetPasswordMutation,
	useConfirmEmailMutation,
	useLazyFetchMeQuery,
	useFetchMeQuery,
	useAuthTwoFactorCodeMutation,
	useGoogleAuthMutation,
	useRegisterAccountMutation,
	useChangePasswordWithTokenMutation,
	useValidateInvitationTokenQuery,
	useResendInvitationTokenMutation,
	useVerifyEmailMutation,
	useResendVerificationEmailMutation,
} = authApi;
export const authApiReducer = authApi.reducer;
export const authApiMiddleware = authApi.middleware;
